import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { FaMoneyCheckAlt } from "react-icons/fa";
import DeleteModal from "components/DeleteModal/DeleteModal";
import DynamicTable from "components/Tables/DynamicTable";
import Input from "shared/Input/Input";
import Pagination from "shared/Pagination/Pagination";
import DashboardMenu from "../DashboardPage/DashboardMenu";
import { deleteContact } from "redux/features/contact/contactSlice";
import TransactionDetailsPage from "./TransactionDetailsPage";
import { itemPerDashborad } from "data/constants";
import { getAllTransactions } from "redux/features/Admin/Stripe/stripeSlice";
import Loading from "components/Loader/Loading";

export interface ListReclamationPageProps {
  className?: string;
}

const ListTransactionAdminPage: FC<ListReclamationPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const transactions = useAppSelector((state) => state?.stripe?.transactionsList);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const itemsPerPage = itemPerDashborad;

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(getAllTransactions());
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const paginatedTransaction = Array.isArray(transactions)
    ? transactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : [];

  const length = Array.isArray(transactions) ? transactions.length : 0;
  const totalPages = Array.isArray(transactions) ? Math.ceil(transactions.length / itemsPerPage) : 0;

  const paginatedSearchedTransactions = Array.isArray(transactions)
    ? transactions
        .filter((item) => {
          const nom = item.amount?.toString() || "";
          const email = item.currency || "";
          return (
            nom.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            email.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
          );
        })
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : [];

  const displayedTransaction = debouncedSearchTerm === "" ? paginatedTransaction : paginatedSearchedTransactions;

  const handleDeleteModalOpen = (serviceAgents: any) => {
    setSelectedTransaction(serviceAgents);
    setShowTransactionDetails(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedTransaction && selectedTransaction?.id !== undefined) {
      setDeleting(true);
      await dispatch(deleteContact(selectedTransaction.id));
      setDeleting(false);
      setDeleteModalOpen(false);
      setIsSearchLoading(true);
      await dispatch(getAllTransactions());
      setIsSearchLoading(false);
    }
  };

  const handleEyeIconClick = (reclamation: any) => {
    setSelectedTransaction(reclamation);
    setShowTransactionDetails(true);
    setDeleteModalOpen(false);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const columns = [
    {
      key: "created",
      label: "Date",
      render: (item: any) => (
        <span className="align-middle">
          {new Date(item?.created * 1000).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}
        </span>
      ),
    },
    {
      key: "id",
      label: "Référence",
      render: (item: any) => <span className="align-middle">{item?.id}</span>,
    },
    {
      key: "destinateur",
      label: "Destinateur",
      render: (item: any) => (
        <span className="align-middle">{item?.account?.business_profile?.name || "N/A"}</span>
      ),
    },
    {
      key: "currency",
      label: "Devise",
      render: (item: any) => <span className="align-middle">{item?.currency?.toUpperCase() || "N/A"}</span>,
    },
    {
      key: "amount",
      label: "Montant",
      render: (item: any) => <span className="align-middle">{item?.amount}</span>,
    },
    // {
    //   key: "actions",
    //   label: "Actions",
    // },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaMoneyCheckAlt className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Transactions
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par montant ou devise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search transactions"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {deleteModalOpen && (
            <DeleteModal
              open={deleteModalOpen}
              handleClose={() => setDeleteModalOpen(false)}
              handleDelete={handleDeleteConfirm}
              category={{
                id: selectedTransaction?.id || 0,
                designation: selectedTransaction?.email || "",
              }}
            />
          )}

          {showTransactionDetails && !deleting && (
            <TransactionDetailsPage
              open={true}
              reclamationData={selectedTransaction}
              handleClose={() => setShowTransactionDetails(false)}
            />
          )}

          <DynamicTable
            columns={columns}
            data={displayedTransaction}
            totalItems={length}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onAction={(action: string, item: any) => {
              if (action === "view") {
                handleEyeIconClick(item);
              } else if (action === "delete") {
                handleDeleteModalOpen(item);
              }
            }}
            totalPages={totalPages}
            availableActions={["delete"]}
          />

          <div className="flex justify-end mt-4">
            <span className="text-sm text-gray-700 dark:text-gray-400">
              {length} élément{length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListTransactionAdminPage;
